import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { CustomResponseEntity } from '../types/common';
import { api } from '../client/axiosClient';
import { getApiUrl } from '../config/apiConfig';

// Interface for data type
export interface DataType {
  id: string;
  name: string;
  validator: string; // Regex pattern for validation
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

// Interface for dynamic property types
export interface DynamicPropertyType {
  id: string;
  name: string;
  dataTypeId: string;
  dataType?: DataType; // Include the full data type object
  displayName: string;
  description?: string;
  defaultValue: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

// LocalStorage keys for storage (not time-based cache)
const DYNAMIC_PROPERTY_TYPES_CACHE_KEY = 'papo_dynamic_property_types_cache';

// Query keys
export const dynamicPropertyTypeKeys = {
  all: ['dynamicPropertyType'] as const,
  types: () => [...dynamicPropertyTypeKeys.all, 'list'] as const,
  type: (id: string) => [...dynamicPropertyTypeKeys.types(), id] as const,
  preload: () => [...dynamicPropertyTypeKeys.all, 'preload'] as const,
};

// Separate function to fetch dynamic property types
export const fetchDynamicPropertyTypes = async (): Promise<CustomResponseEntity<DynamicPropertyType[]>> => {
  const response = await api.get<CustomResponseEntity<DynamicPropertyType[]>>(getApiUrl('/dynamicpropertytype'));
  return response;
};

// Separate function to fetch data types
export const fetchDataTypes = async (): Promise<CustomResponseEntity<DataType[]>> => {
  const response = await api.get<CustomResponseEntity<DataType[]>>(getApiUrl('/datatype'));
  return response;
};

// Function to enrich dynamic properties with their data types
export const enrichDynamicPropertiesWithDataTypes = (
  dynamicProps: DynamicPropertyType[],
  dataTypes: DataType[]
): DynamicPropertyType[] => {
  return dynamicProps.map(dynamicProp => {
    const matchingDataType = dataTypes.find(dataType => dataType.id === dynamicProp.dataTypeId);
    return {
      ...dynamicProp,
      dataType: matchingDataType
    };
  });
};

// Function to save data to localStorage (for backup only, not time-based cache)
const saveToLocalStorage = (data: CustomResponseEntity<DynamicPropertyType[]>): void => {
  try {
    localStorage.setItem(DYNAMIC_PROPERTY_TYPES_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

// Hook to preload dynamic property types
export const usePreloadDynamicPropertyTypes = (
  options?: UseQueryOptions<CustomResponseEntity<DynamicPropertyType[]>>
) => {
  return useQuery({
    queryKey: dynamicPropertyTypeKeys.preload(),
    queryFn: async () => {
      // Always fetch fresh data from API
      const result = await fetchDynamicPropertyTypes();
      // Save to localStorage for backup/persistence (not time-based cache)
      saveToLocalStorage(result);
      return result;
    },
    staleTime: 0,               // Always consider data stale - refetch every time
    gcTime: 5 * 60 * 1000,      // Keep in memory for 5 minutes after component unmount
    refetchOnMount: true,       // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    ...options,
  });
};

// Hook to fetch all dynamic property types
export const useDynamicPropertyTypes = (
  options?: UseQueryOptions<CustomResponseEntity<DynamicPropertyType[]>>
) => {
  return useQuery({
    queryKey: dynamicPropertyTypeKeys.types(),
    queryFn: async () => {
      try {
        // Fetch both data types and dynamic property types in parallel
        const [dynamicPropsResponse, dataTypesResponse] = await Promise.all([
          fetchDynamicPropertyTypes(),
          fetchDataTypes()
        ]);
        
        // Enrich dynamic properties with their data types
        if (dynamicPropsResponse.data && dataTypesResponse.data) {
          const enrichedDynamicProps = enrichDynamicPropertiesWithDataTypes(
            dynamicPropsResponse.data,
            dataTypesResponse.data
          );
          
          return {
            ...dynamicPropsResponse,
            data: enrichedDynamicProps
          };
        }
        
        return dynamicPropsResponse;
      } catch (error) {
        // Fallback to just dynamic property types without data types
        return await fetchDynamicPropertyTypes();
      }
    },
    ...options,
  });
};

export interface UpdateDynamicPropertyTypePayload {
  id: string;
  name: string;
  dataTypeId: string;
  displayName?: string;
  description?: string;
  defaultValue?: string;
};

export const updateDynamicPropertyType = async (
  payload: UpdateDynamicPropertyTypePayload
): Promise<CustomResponseEntity<DynamicPropertyType>> => {
  const response = await api.put<CustomResponseEntity<DynamicPropertyType>>(
    getApiUrl(`/dynamicpropertytype/${payload.id}`),
    {
      name: payload.name,
      dataTypeId: payload.dataTypeId,
      displayName: payload.displayName,
      description: payload.description,
      defaultValue: payload.defaultValue,
    }
  );
  return response;
};

export const useUpdateDynamicPropertyType = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CustomResponseEntity<DynamicPropertyType>,
    Error,
    UpdateDynamicPropertyTypePayload
  >({
    mutationFn: updateDynamicPropertyType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicPropertyTypeKeys.all });
    },
  });
};
